<script setup lang="ts">
  import { computed, inject, onBeforeMount, ref, useCssModule, useTemplateRef } from 'vue';

  import usePaginationStats from '../../composables/usePaginationStats/usePaginationStats';
  import { t } from '../../locale';
  import Button from '../Button/Button.vue';
  import Checkbox from '../Checkbox/Checkbox.vue';
  import { DATA_VIEW_INJECTION } from '../DataView/DataView.vue';
  import Icon from '../Icon/Icon.vue';
  import IconLabel from '../IconLabel/IconLabel.vue';
  import MoreActions from '../MoreActions/MoreActions.vue';
  import { ToolbarRadiuses } from './DataViewToolbar.types';
  import { useActionsWidth } from './useActionsWidth';

  export interface DataViewToolbarProps {
    /**
     * When DataView has `variant="table"` and Table has `is-selectable` enabled, it provides a way to flag
     * a checkbox whenever all rows are selected, just like the standalone version on TableHeaderRow.
     */
    allRowsSelected?: boolean;

    /**
     * Hides the page statistics (ex: "1 - 12 of 20") in the toolbar
     */
    hidePageStats?: boolean;

    /**
     * Controls the corners of DataViewToolbar with the "border-radius" CSS property. The default value is "rounded".
     */
    radius?: ToolbarRadiuses;

    /**
     * When DataView has variant="table" and Table has `is-selectable` enabled, it provides a way to flag
     * a checkbox whenever one or more, but not all rows are selected, just like the standalone
     * version on TableHeaderRow.
     */
    someRowsSelected?: boolean;

    /**
     * The number of selected items. Used to display selected items count in the toolbar.
     */
    selectedItemsCount?: number;

    /**
     * When DataView has tabs above it, we need to remove the top left rounded corner
     */
    hasTabsAbove?: boolean;
  }

  const props = withDefaults(defineProps<DataViewToolbarProps>(), {
    allRowsSelected: false,
    hidePageStats: false,
    radius: undefined,
    someRowsSelected: false,
    selectedItemsCount: undefined,
    hasTabsAbove: false,
  });

  const emit = defineEmits<{
    (e: 'select'): void;
  }>();

  // Refs for width calculation
  const toolbarRef = useTemplateRef('toolbarEl');
  const paginationRef = useTemplateRef('paginationEl');
  const checkboxRef = useTemplateRef('checkboxEl');

  const {
    isWithinModule,
    variant: dataViewVariant,
    density,
    currentPage,
    hasToolbar,
    isPaginateNextDisabled,
    isPaginationEnabled,
    isSelectable,
    pageCount,
    pageSize,
    totalDataCount,
    goPrevPage,
    goNextPage,
    isEmpty,
    isLoading,
  } = inject(DATA_VIEW_INJECTION.key, DATA_VIEW_INJECTION.defaults);

  const checkboxKey = ref(0);
  const classes = useCssModule();

  const { actionsWidth } = useActionsWidth({
    toolbarEl: toolbarRef,
    paginationEl: paginationRef,
    checkboxEl: checkboxRef,
    isPaginationEnabled,
    isSelectable,
    isEmpty,
  });

  const computedRadius = computed<ToolbarRadiuses>(() => {
    if (props.radius) {
      return props.radius;
    }

    if (isWithinModule.value) {
      return 'none';
    }

    if (dataViewVariant.value === 'table') {
      if (props.hasTabsAbove) {
        return 'rounded-top-right';
      }

      return 'rounded-top';
    }

    return 'rounded';
  });

  const pageStats = usePaginationStats({ currentPage, pageSize, totalItems: totalDataCount });

  function onSelect() {
    emit('select');

    /**
     * In Vue (as of this writing), if a user checks a native checkbox but the v-model is `false` and does not change from `false`, then the checkbox will display as checked even though the v-model is still `false`.
     * Forcing a re-render with a `key` change allows the checkbox to stay unchecked if its v-model is still `false` and its v-model not been changed.
     * To verify that this is necessary, test the "select all" checkbox in the SelectionWithSomeDisabled story in Table.story.ts with and without the `key` attribute.
     */
    checkboxKey.value++;
  }

  onBeforeMount(() => {
    if (hasToolbar) {
      hasToolbar.value = true;
    }
  });
</script>

<template>
  <div
    ref="toolbarEl"
    class="stash-data-view-toolbar flex min-h-15 items-center justify-between bg-white py-1.5 shadow"
    :class="{
      'border-t border-ice-200': isWithinModule,
      'rounded-tr': computedRadius === 'rounded-top-right',
      rounded: computedRadius === 'rounded',
      'rounded-t': computedRadius === 'rounded-top',
      'mb-3': dataViewVariant !== 'table' && !isWithinModule,
      'px-3': density === 'compact',
      'px-3 lg:px-6': density === 'comfortable',
    }"
    data-test="stash-data-view-toolbar"
  >
    <div class="flex place-items-center">
      <Checkbox
        v-if="isSelectable && !isEmpty"
        ref="checkboxEl"
        :key="checkboxKey"
        :checked="props.allRowsSelected"
        :indeterminate="props.someRowsSelected && !props.allRowsSelected"
        :title="t('ll.selectAll')"
        :disabled="isLoading"
        @update:indeterminate="onSelect"
        @update:checked="onSelect"
      />
      <template v-if="$slots['more-actions']">
        <MoreActions
          more-button-align="together"
          :width="actionsWidth || undefined"
          actions-container-class="flex items-center gap-x-2 lg:mr-6 lg:gap-x-6"
        >
          <template #toggle="{ toggle }">
            <Button
              icon-label
              class="text-blue-500"
              data-test="stash-data-view-sort-button|sort-menu-button"
              @click="toggle"
            >
              <IconLabel icon="ellipsis" stacked> More </IconLabel>
            </Button>
          </template>
          <slot></slot>
          <template #more-actions>
            <slot name="more-actions"></slot>
          </template>
        </MoreActions>
      </template>
      <template v-else>
        <div :class="classes.actions" class="stash-data-view-toolbar__actions flex items-center gap-x-2 lg:gap-x-4">
          <slot></slot>
        </div>
      </template>
    </div>

    <!-- Pagination -->
    <div
      v-if="isPaginationEnabled"
      ref="paginationEl"
      class="stash-data-view-toolbar__pagination flex shrink-0 items-center"
    >
      <slot name="selected-stats">
        <span v-if="props.someRowsSelected && props.selectedItemsCount !== undefined" class="mx-1.5">
          {{ t('ll.listView.numberOfTotalSelected', { num: props.selectedItemsCount, total: totalDataCount }) }}
        </span>
      </slot>
      <slot name="page-stats">
        <span v-if="!props.hidePageStats" class="mx-1.5 hidden md:block">
          {{ t('ll.pageStats', pageStats) }}
        </span>
      </slot>
      <template v-if="pageCount > 1">
        <Button
          icon
          :aria-label="t('ll.previous')"
          class="size-9 p-1.5"
          data-test="button|prev-page"
          :disabled="isLoading || currentPage === 1"
          @click="goPrevPage({ shouldEmit: true })"
        >
          <Icon name="chevron-left" size="dense" />
        </Button>
        <Button
          icon
          :aria-label="t('ll.next')"
          class="size-9 p-1.5"
          data-test="button|next-page"
          :disabled="isLoading || isPaginateNextDisabled || currentPage === pageCount"
          @click="goNextPage({ shouldEmit: true })"
        >
          <Icon name="chevron-right" size="dense" />
        </Button>
      </template>
    </div>
  </div>
</template>

<style module>
  @layer utilities {
    .actions :global(.stash-button) {
      min-width: auto;
      padding: 0 0.5rem;

      &:first-child {
        margin-left: -0.5rem;
      }
    }
  }
</style>
